/**
 * Win Rate Analytics — Phase 1
 *
 * KPIs: total quotes sent, approved, rejected, conversion rate
 * Charts: monthly stacked bar (sent/approved/rejected), donut for current period
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Send, CheckCircle2, XCircle, Percent,
  ArrowLeft, RefreshCw, AlertCircle,
} from 'lucide-react';
import { Button }    from '../components/ui/button';
import { toast }     from 'sonner';
import axios from 'axios';
import ChartShell    from '../features/reports/components/ChartShell';
import KpiCard       from '../features/reports/components/KpiCard';
import PeriodSelector from '../features/reports/components/PeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs space-y-1">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// Donut segment colors
const PIE_COLORS = {
  Won:     '#22c55e',
  Lost:    '#ef4444',
  Pending: '#f59e0b',
};

const ReportsWinRate = () => {
  const [period,  setPeriod]  = useState('this_month');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res   = await axios.get(`${API}/api/reports/win-rate?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load win rate report';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const pieData = data ? [
    { name: 'Won',     value: data.approved,  color: PIE_COLORS.Won     },
    { name: 'Lost',    value: data.rejected,  color: PIE_COLORS.Lost    },
    { name: 'Pending', value: data.pending,   color: PIE_COLORS.Pending },
  ].filter(d => d.value > 0) : [];

  const periodLabel = {
    this_month: 'This Month',
    last_month: 'Last Month',
    last_3m:    'Last 3 Months',
    last_6m:    'Last 6 Months',
    this_year:  'This Year',
  }[period];

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">

      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <Link
            to="/reports"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Reports
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">Win Rate Analytics</h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(period)}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="win-rate-kpi-grid">
        <KpiCard
          label="Quotes Sent"
          value={loading ? '…' : String(data?.totalSent ?? 0)}
          sub={`in ${periodLabel.toLowerCase()}`}
          icon={Send}
          iconColor="text-blue-500"
          iconBg="bg-blue-50"
          loading={loading}
          testId="kpi-quotes-sent"
        />
        <KpiCard
          label="Won (Approved)"
          value={loading ? '…' : String(data?.approved ?? 0)}
          sub="Booked or approved"
          icon={CheckCircle2}
          iconColor="text-green-600"
          iconBg="bg-green-50"
          loading={loading}
          testId="kpi-approved"
        />
        <KpiCard
          label="Lost (Rejected)"
          value={loading ? '…' : String(data?.rejected ?? 0)}
          sub="Client declined"
          icon={XCircle}
          iconColor="text-red-500"
          iconBg="bg-red-50"
          loading={loading}
          testId="kpi-rejected"
        />
        <KpiCard
          label="Conversion Rate"
          value={loading ? '…' : `${data?.conversionRate ?? 0}%`}
          sub="Won ÷ total sent"
          icon={Percent}
          iconColor="text-orange-500"
          iconBg="bg-orange-50"
          loading={loading}
          testId="kpi-conversion-rate"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Stacked bar by month (2/3 width) */}
        <div className="lg:col-span-2">
          <ChartShell
            title="Quote Activity — Last 6 Months"
            subtitle="Sent, won, and lost quotes by month"
            loading={loading}
            empty={!loading && (data?.byMonth ?? []).every(m => m.sent === 0)}
            height={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={data?.byMonth ?? []}
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip content={<CountTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sent"     name="Sent"     fill="#93c5fd" radius={[3, 3, 0, 0]} maxBarSize={40} />
                <Bar dataKey="approved" name="Won"      fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={40} />
                <Bar dataKey="rejected" name="Lost"     fill="#f87171" radius={[3, 3, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartShell>
        </div>

        {/* Donut chart (1/3 width) */}
        <ChartShell
          title="Period Breakdown"
          subtitle={`${periodLabel} quote outcomes`}
          loading={loading}
          empty={!loading && pieData.length === 0}
          height={280}
        >
          {pieData.length > 0 && (
            <div className="flex flex-col items-center w-full h-full">
              <PieChart width={180} height={180}>
                <Pie
                  data={pieData}
                  cx={90}
                  cy={90}
                  innerRadius={52}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </PieChart>
              {/* Legend */}
              <div className="flex flex-col gap-1.5 mt-1 w-full px-4">
                {pieData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                      <span className="text-gray-600">{d.name}</span>
                    </div>
                    <span className="font-semibold text-gray-800">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartShell>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ReportsWinRate;
